import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, TextField, InputAdornment, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Switch, Alert, Tooltip } from '@mui/material';
import { Search, Add, Edit, Delete, Business, Email, Phone, Web } from '@mui/icons-material';
import { companiesAPI } from '../services/api';

const Companies = () => {
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    logo: null
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadCompanies(); }, []);

  const loadCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      if (response.data.success) {
        setCompanies(response.data.data);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Company name is required' });
      return false;
    }
    if (!formData.address.trim()) {
      setMessage({ type: 'error', text: 'Company address is required' });
      return false;
    }
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      setMessage({ type: 'error', text: 'Invalid email format' });
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const formDataToSend = new FormData();

      // Always include required fields
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('address', formData.address.trim());

      // Include optional fields (convert empty strings to null)
      if (formData.phone?.trim()) formDataToSend.append('phone', formData.phone.trim());
      if (formData.email?.trim()) formDataToSend.append('email', formData.email.trim());
      if (formData.website?.trim()) formDataToSend.append('website', formData.website.trim());
      if (formData.logo) formDataToSend.append('logo', formData.logo);


      if (isEditMode) {
        await companiesAPI.update(selectedCompany.id, formDataToSend);
        setMessage({ type: 'success', text: 'Company updated successfully' });
      } else {
        await companiesAPI.create(formDataToSend);
        setMessage({ type: 'success', text: 'Company created successfully' });
      }
      setShowDialog(false);
      resetForm();
      loadCompanies();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Operation failed' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', phone: '', email: '', website: '', logo: null });
    setIsEditMode(false);
    setSelectedCompany(null);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    resetForm();
  };

  const handleEdit = (company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name || '',
      address: company.address || '',
      phone: company.phone || '',
      email: company.email || '',
      website: company.website || '',
      logo: null
    });
    setIsEditMode(true);
    setShowDialog(true);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      await companiesAPI.delete(selectedCompany.id);
      setMessage({ type: 'success', text: 'Company deleted successfully' });
      setShowDeleteDialog(false);
      setSelectedCompany(null);
      loadCompanies();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Delete failed' });
    }
  };

  const handleToggleActive = async (company) => {
    try {
      await companiesAPI.update(company.id, { is_active: !company.is_active });
      setMessage({ type: 'success', text: `Company ${company.is_active ? 'deactivated' : 'activated'} successfully` });
      loadCompanies();
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Operation failed' });
    }
  };

  const openNewDialog = () => {
    setFormData({ name: '', address: '', phone: '', email: '', website: '', logo: null });
    setIsEditMode(false);
    setSelectedCompany(null);
    setShowDialog(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFormData({ ...formData, logo: file });
  };

  return (
    <Box sx={{ bgcolor: '#0A1929', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#D4AF37' }}>
            Company Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openNewDialog}
            sx={{ bgcolor: '#D4AF37', color: 'rgb(10, 25, 41)', '&:hover': { bgcolor: '#C19B2E' } }}
          >
            Add Company
          </Button>
        </Box>

        {message.text && (
          <Alert
            severity={message.type}
            onClose={() => setMessage({ type: '', text: '' })}
            sx={{
              mb: 3,
              bgcolor: 'rgb(10, 25, 41)',
              color: message.type === 'success' ? '#81C784' : '#EF9A9A',
              border: `1px solid ${message.type === 'success' ? '#81C784' : '#EF9A9A'}`,
              '& .MuiAlert-icon': { color: message.type === 'success' ? '#81C784' : '#EF9A9A' }
            }}
          >
            {message.text}
          </Alert>
        )}

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search companies by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#D4AF37' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              bgcolor: 'rgb(10, 25, 41)',
              color: '#ffffff',
              '& fieldset': { borderColor: '#D4AF37' },
              '&:hover fieldset': { borderColor: '#C19B2E' },
            },
          }}
        />

        {/* Companies Table */}
        <Card sx={{ bgcolor: 'rgb(10, 25, 41)', border: '1px solid #D4AF37', borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} sx={{ maxHeight: 600, bgcolor: 'transparent', boxShadow: 'none' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2A3E24' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', minWidth: 200 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', minWidth: 200 }}>Address</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', minWidth: 150 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', minWidth: 120 }}>Website</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', minWidth: 80 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#D4AF37', minWidth: 100 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies
                    .filter(company => company.name.toLowerCase().includes(search.toLowerCase()))
                    .map((company) => (
                    <TableRow key={company.id} hover sx={{ '&:hover': { bgcolor: 'rgba(212,175,55,0.05)' } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: '#D4AF37',
                              color: 'rgb(10, 25, 41)',
                              fontWeight: 600,
                              fontSize: '1rem'
                            }}
                            src={company.logo ? `${process.env.REACT_APP_API_URL || 'http://localhost:5076'}/uploads/companies/${company.logo}` : undefined}
                          >
                            {company.name[0]}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: '#FFFFFF' }}>
                              {company.name}
                            </Typography>
                            {company.email && (
                              <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                                {company.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#B4C4A8' }}>
                          {company.address}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {company.phone && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Phone sx={{ fontSize: 14, color: '#D4AF37' }} />
                              <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                                {company.phone}
                              </Typography>
                            </Box>
                          )}
                          {company.email && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Email sx={{ fontSize: 14, color: '#D4AF37' }} />
                              <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                                {company.email}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {company.website && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Web sx={{ fontSize: 14, color: '#D4AF37' }} />
                            <Typography variant="caption" sx={{ color: '#B4C4A8' }}>
                              {company.website}
                            </Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Switch
                          size="small"
                          checked={company.is_active}
                          onChange={() => handleToggleActive(company)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#81C784' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#81C784' },
                            '& .MuiSwitch-track': { backgroundColor: '#EF9A9A' },
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title="Edit Company">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(company)}
                            sx={{ color: '#D4AF37', mr: 1, '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' } }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Company">
                          <IconButton
                            size="small"
                            onClick={() => { setSelectedCompany(company); handleDelete(); }}
                            sx={{ color: '#EF9A9A', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                  {companies.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ textAlign: 'center', py: 6 }}>
                        <Typography sx={{ color: '#B4C4A8' }}>
                          {search ? 'No companies match your search' : 'No companies found'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Add/Edit Company Dialog */}
        <Dialog
          open={showDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600, borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
            {isEditMode ? 'Edit Company' : 'Add New Company'}
          </DialogTitle>
          <DialogContent sx={{ pb: 1, pt: 2 }}>
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr'
              },
              gap: 2,
              mt: 1
            }}>
              <TextField
                fullWidth
                label="Company Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField
                fullWidth
                label="Website"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <TextField
                fullWidth
                label="Company Address"
                multiline
                rows={3}
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                required
                sx={{ gridColumn: '1 / -1' }}
                InputLabelProps={{ sx: { color: '#B4C4A8' } }}
                InputProps={{ sx: { color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' } } }}
              />
              <Box sx={{ gridColumn: '1 / -1' }}>
                <input
                  accept="image/*"
                  className="hidden-input"
                  id="logo-upload"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="logo-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<Business />}
                    sx={{
                      color: '#D4AF37',
                      borderColor: '#D4AF37',
                      '&:hover': { borderColor: '#C19B2E', bgcolor: 'rgba(212,175,55,0.1)' }
                    }}
                  >
                    Upload Company Logo
                  </Button>
                </label>
                {formData.logo && (
                  <Typography variant="caption" sx={{ ml: 2, color: '#81C784' }}>
                    {formData.logo.name}
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>Cancel</Button>
            <Button onClick={handleSave} variant="contained" disabled={loading}>
              {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Save')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          PaperProps={{
            sx: {
              bgcolor: 'rgb(10, 25, 41)',
              border: '2px solid #D4AF37',
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 600 }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#FFFFFF' }}>
              Are you sure you want to delete "{selectedCompany?.name}"?
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
            <Button onClick={confirmDelete} variant="contained" color="error">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Companies;